/**
 * =============================================================================
 * Hook: useReferralReferralDetails
 * Versão: 1.0.0
 *
 * Descrição:
 *   Carrega TODOS os detalhes de uma INDICAÇÃO em uma única chamada via RPC
 *   `get_referral_referral_details`. Espelha o padrão arquitetural de
 *   `useReferralCommissionDetails` e `useReferralPayoutDetails`:
 *
 *   - 100% server-side: nada de SELECT direto na tabela.
 *   - Realtime: invalida o cache em UPDATE de `affiliate_referrals` (filtro id)
 *     e em INSERT/UPDATE de `affiliate_commissions` (filtro referral_id) — para
 *     refletir conversões/comissões geradas em tempo real na timeline.
 *   - Habilita-se apenas quando há `referralId` E o Sheet está aberto.
 *
 * IMPORTANTE — UUIDs reais:
 *   A RPC retorna UUIDs COMPLETOS (sem mascaramento) para preservar a
 *   navegação para entidades CRM (Negócio, Contato, Vendedor, Comissão).
 *   A UI deve aplicar `shortId()` apenas para EXIBIÇÃO.
 *
 * RELEASE:
 *   2026-04-30 v1.0.0 - Criação inicial.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ReferralAdminStatus } from '@/hooks/useReferralReferralsAdmin';
import type { CommissionStatus, RewardType } from '@/hooks/useReferralCommissionsPaginated';

const logger = createLogger('useReferralReferralDetails');

/** Bloco principal "referral" com joins. */
export interface ReferralDetailsReferral {
  id: string;
  tenant_id: string;
  status: ReferralAdminStatus;
  lead_name: string | null;
  lead_email: string | null;
  lead_phone: string | null;
  lead_message: string | null;
  lead_source: string | null;
  conversion_value: number | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  qualified_at: string | null;
  qualified_by: string | null;
  converted_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  click_id: string | null;
  link_id: string | null;
  // Programa / Campanha
  program_id: string | null;
  program_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  // Afiliado
  affiliate_id: string | null;
  affiliate_name: string | null;
  affiliate_email: string | null;
  affiliate_avatar_url: string | null;
  affiliate_phone: string | null;
  // CRM Contato
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  // CRM Negócio
  deal_id: string | null;
  deal_title: string | null;
  deal_value: number | null;
  deal_status: 'open' | 'won' | 'lost' | null;
  deal_phase_id: string | null;
  deal_phase_name: string | null;
  deal_phase_color: string | null;
  deal_phase_sort_order: number | null;
  deal_pipeline_id: string | null;
  deal_pipeline_total_phases: number | null;
  deal_category_name: string | null;
  // v1.0.3 — adicionados em 2026-04-30
  deal_workspace_name: string | null;
  deal_source: string | null;
  deal_seller_id: string | null;
  deal_seller_name: string | null;
  deal_seller_avatar_url: string | null;
  deal_customer_id: string | null;
  deal_customer_name: string | null;
  // v1.0.4 — documento do cliente vinculado (CPF ou CNPJ)
  deal_customer_cpf: string | null;
  deal_customer_cnpj: string | null;
  // Quem qualificou
  qualified_by_name: string | null;
  qualified_by_email: string | null;
}

/** Comissão gerada a partir desta indicação (resumo). */
export interface ReferralDetailsCommission {
  id: string;
  status: CommissionStatus;
  reward_type: RewardType;
  reward_value: number | null;
  currency: string | null;
  base_value: number | null;
  created_at: string;
  approved_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  payout_id: string | null;
}

/** Tipos de evento sintéticos da timeline da indicação. */
export type ReferralTimelineAction =
  | 'created'
  | 'qualified'
  | 'crm_contact_created'
  | 'crm_deal_created'
  | 'crm_processing_error'
  | 'crm_reprocessed'
  | 'converted'
  | 'commission_generated'
  | 'lost';

export interface ReferralTimelineEvent {
  id: string;
  action: ReferralTimelineAction;
  reference_id: string | null;
  reference_type: 'deal' | 'contact' | 'commission' | null;
  performer_name: string | null;
  performer_email: string | null;
  reason: string | null;
  notes: string | null;
  created_at: string;
}

/** Estrutura completa retornada pela RPC `get_referral_referral_details`. */
export interface ReferralDetailsResult {
  referral: ReferralDetailsReferral;
  commissions: ReferralDetailsCommission[];
  timeline: ReferralTimelineEvent[];
}

/**
 * Hook principal — busca detalhes da indicação + assina realtime.
 *
 * @param referralId UUID da indicação (`null`/`undefined` desabilita).
 * @param open       Se o Sheet está aberto (controla enabled e subscription).
 */
export function useReferralReferralDetails(
  referralId: string | undefined | null,
  open: boolean,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // ── Realtime ──
  useEffect(() => {
    if (!referralId || !open) return;

    const channel = supabase
      .channel(`referral-rt-${referralId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'affiliate_referrals',
          filter: `id=eq.${referralId}`,
        },
        () => {
          logger.debug('Indicação alterada via realtime, invalidando cache', { referralId });
          queryClient.invalidateQueries({
            queryKey: ['referral-referral-details', referralId],
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `referral_id=eq.${referralId}`,
        },
        () => {
          logger.debug('Comissão da indicação alterada via realtime', { referralId });
          queryClient.invalidateQueries({
            queryKey: ['referral-referral-details', referralId],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [referralId, open, queryClient]);

  return useQuery({
    queryKey: ['referral-referral-details', referralId],
    enabled: !!referralId && open,
    queryFn: async (): Promise<ReferralDetailsResult> => {
      if (!referralId) throw new Error('referralId é obrigatório');

      // Casting até o `types.ts` ser regenerado com a nova RPC.
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_referral_details',
        {
          p_referral_id: referralId,
          p_tenant_hint: currentTenant?.id ?? null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar detalhes da indicação via RPC', error);
        throw error;
      }

      return data as ReferralDetailsResult;
    },
    staleTime: 30 * 1000,
  });
}
